import { ReviewStars } from '@components/ReviewStars/ReviewStars'
import { Box, Paragraph, Stack, Text, TextButton } from '@ui'
import { useAgentFeedbackSummary, useAgentPromotionStatus } from 'use-towns-client'
import { Address } from 'viem'

type Props = {
    userId: string
    botAppAddress?: Address
    onClick: () => void
}

export const BotReviews = (props: Props) => {
    const { botAppAddress, onClick } = props
    const { data: promotionStatus, isLoading: isLoadingPromotion } =
        useAgentPromotionStatus(botAppAddress)
    const isPromoted = promotionStatus?.isPromoted ?? false
    const agentId = promotionStatus?.agentId

    // Fetch feedback summary only if bot is promoted
    const { data: feedbackSummary, isLoading: isLoadingFeedback } = useAgentFeedbackSummary(agentId)

    const rating = feedbackSummary?.averageRating ?? 0
    const reviewCount = feedbackSummary?.count ?? 0

    // Don't show anything while loading promotion status
    if (isLoadingPromotion) {
        return null
    }

    // If bot is not promoted, show placeholder explaining why reviews aren't available
    if (!isPromoted) {
        return (
            <Stack padding gap="paragraph" background="level2" rounded="sm">
                <Text>Reviews</Text>
                <Paragraph color="gray2">
                    This bot must be promoted before it can receive reviews.
                </Paragraph>
            </Stack>
        )
    }

    // Bot is promoted, show reviews UI with "Write a Review" button
    return (
        <Stack
            padding
            hoverable
            rounded="sm"
            gap="paragraph"
            background="level2"
            cursor="pointer"
            onClick={onClick}
        >
            <Stack horizontal gap="sm" alignItems="center">
                <Box grow>
                    <Text>
                        Reviews{reviewCount > 0 && !isLoadingFeedback ? ` (${reviewCount})` : ''}
                    </Text>
                </Box>
                <Box>
                    <TextButton size="lg">Write a Review</TextButton>
                </Box>
            </Stack>
            {reviewCount > 0 && !isLoadingFeedback && <ReviewStars rating={rating} size={24} />}
            {reviewCount === 0 && !isLoadingFeedback && (
                <Paragraph color="gray2">No reviews yet. Be the first!</Paragraph>
            )}
        </Stack>
    )
}
